// GOV-001 P9 — Approval Requests browser (governance view).
//
// Read-only view over EVERY engine request — any status, any module —
// with filters and the full tier timeline. Auditors and admins use
// this; acting on a request still happens only in the assignee's own
// My Approvals inbox.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Card, DatePicker, Descriptions, Drawer, Select, Space, Table, Tag,
  Typography,
} from 'antd'
import { FileSearchOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'

const { Text, Title } = Typography

interface Req {
  id: string
  entity_name: string
  entity_id: string | null
  action: string
  status: string
  current_tier: number
  total_tiers: number
  requested_by_name: string | null
  requested_at: string
  assignee_role: string | null
  amount_value: number | null
  risk_level: string | null
  reason: string | null
  payload: Record<string, unknown> | null
  due_at: string | null
  escalated_at: string | null
  escalated_to_role: string | null
  decided_by_name: string | null
  decided_at: string | null
  rejection_reason: string | null
  timeline?: { tier: number; decision: string; acted_by_name: string | null
    acted_at: string; remarks: string | null }[]
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'processing', APPROVED: 'success', REJECTED: 'error',
  CANCELLED: 'default', ESCALATED: 'warning', EXPIRED: 'default',
}

const ACTIONS = [
  'cost.rate.update', 'production.standard.update', 'inventory.stock.adjust',
  'hr.salary_structure.update', 'hr.employee.activate',
  'admin.user_access.grant', 'production.reopen', 'grn.correct',
  'recipe.revoke', 'payroll.recompute', 'sales.cancel', 'po.cancel',
  'voucher.reverse',
]

export default function ApprovalRequestsAdminPage() {
  const [status, setStatus] = useState<string | undefined>()
  const [action, setAction] = useState<string | undefined>()
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [page, setPage] = useState(1)
  const [detailId, setDetailId] = useState<string | null>(null)

  const listQ = useQuery<{ total: number; items: Req[] }>({
    queryKey: ['approval-requests-admin', status, action, range, page],
    queryFn: () => apiClient.get('/admin/approvals/requests', {
      params: {
        status, action, page, size: 25,
        date_from: range?.[0]?.format('YYYY-MM-DD'),
        date_to: range?.[1]?.format('YYYY-MM-DD'),
      },
    }).then((r: any) => r.data),
  })

  const detailQ = useQuery<Req>({
    queryKey: ['approval-request-admin-detail', detailId],
    queryFn: () => apiClient.get(`/admin/approvals/requests/${detailId}`)
      .then((r: any) => r.data),
    enabled: !!detailId,
  })

  const columns: any[] = [
    {
      title: 'Action', dataIndex: 'action', key: 'action', width: 220,
      render: (v: string, r: Req) =>
        <a onClick={() => setDetailId(r.id)}><Text code>{v}</Text></a>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v: string, r: Req) => (
        <Space size={4}>
          <Tag color={STATUS_COLOR[v]}>{v}</Tag>
          {r.escalated_at && <Tag color="warning">ESC</Tag>}
        </Space>
      ),
    },
    {
      title: 'Tier', key: 'tier', width: 70,
      render: (_: unknown, r: Req) => `${r.current_tier}/${r.total_tiers}`,
    },
    { title: 'Requested By', dataIndex: 'requested_by_name', key: 'rb', width: 150 },
    {
      title: 'When', dataIndex: 'requested_at', key: 'when', width: 140,
      render: (v: string) => dayjs(v).format('DD MMM YYYY HH:mm'),
    },
    {
      title: 'Assigned To', dataIndex: 'assignee_role', key: 'assignee',
      width: 140, render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Value', dataIndex: 'amount_value', key: 'value', width: 110,
      render: (v: number | null) =>
        v != null ? `₹${v.toLocaleString('en-IN')}` : '—',
    },
    {
      title: 'Decided By', dataIndex: 'decided_by_name', key: 'db',
      width: 140, render: (v: string | null) => v ?? '—',
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
  ]

  const d = detailQ.data

  return (
    <div style={{ padding: 16 }}>
      <Space align="center" style={{ marginBottom: 12 }}>
        <FileSearchOutlined style={{ fontSize: 22 }} />
        <Title level={4} style={{ margin: 0 }}>Approval Requests — All</Title>
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select allowClear placeholder="Status" style={{ width: 140 }}
            value={status} onChange={v => { setStatus(v); setPage(1) }}
            options={Object.keys(STATUS_COLOR).map(v => ({ value: v, label: v }))} />
          <Select allowClear showSearch placeholder="Action" style={{ width: 260 }}
            value={action} onChange={v => { setAction(v); setPage(1) }}
            options={ACTIONS.map(v => ({ value: v, label: v }))} />
          <DatePicker.RangePicker value={range as never}
            onChange={v => { setRange(v as never); setPage(1) }} />
        </Space>
      </Card>

      <Table
        rowKey="id" size="small" loading={listQ.isLoading}
        dataSource={listQ.data?.items ?? []} columns={columns}
        scroll={{ x: 1300 }}
        pagination={{
          current: page, pageSize: 25, total: listQ.data?.total ?? 0,
          onChange: setPage, showSizeChanger: false,
          showTotal: t => `${t} requests`,
        }}
      />

      <Drawer
        open={!!detailId} width={560}
        title={d ? d.action : 'Request'}
        onClose={() => setDetailId(null)}
      >
        {d && (
          <>
            <Descriptions column={2} size="small" bordered
              style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[d.status]}>{d.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Tier">
                {d.current_tier} / {d.total_tiers}
              </Descriptions.Item>
              <Descriptions.Item label="Requested By">
                {d.requested_by_name ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="At">
                {dayjs(d.requested_at).format('DD MMM YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Assigned To">
                {d.assignee_role ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Value">
                {d.amount_value != null
                  ? `₹${d.amount_value.toLocaleString('en-IN')}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Reason" span={2}>
                {d.reason ?? '—'}
              </Descriptions.Item>
              {d.rejection_reason && (
                <Descriptions.Item label="Rejection" span={2}>
                  <Text type="danger">{d.rejection_reason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
            <Card size="small" title="Payload" style={{ marginBottom: 16 }}>
              <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: 0 }}>
                {JSON.stringify(d.payload, null, 2)}
              </pre>
            </Card>
            <Card size="small" title="Timeline">
              {(d.timeline ?? []).length === 0 && (
                <Text type="secondary">No decisions yet</Text>
              )}
              {(d.timeline ?? []).map((t, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <Tag color={t.decision === 'APPROVE' ? 'green'
                    : t.decision === 'REJECT' ? 'red' : 'default'}>
                    {t.decision}
                  </Tag>
                  <Text strong>{t.acted_by_name ?? 'System'}</Text>
                  <Text type="secondary">
                    {' '}· tier {t.tier} · {dayjs(t.acted_at).format('DD MMM HH:mm')}
                  </Text>
                  {t.remarks && <div><Text type="secondary">{t.remarks}</Text></div>}
                </div>
              ))}
            </Card>
          </>
        )}
      </Drawer>
    </div>
  )
}
